import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useData } from "../hooks/useData";
import DateRangePicker from "../components/heatmap/DateRangePicker";
import { getPartsInTZ, startOfDayInTZ, endOfDayInTZ } from "../lib/timezone";
import { navigateDateRange as calcNavigateDateRange } from "../utils/dateRange";
import SetterPerformanceTab from "../components/dm/SetterPerformanceTab";
import DMHealthTab from "../components/dm/DMHealthTab";
import { PatternReviewSection } from "../components/dm/PatternReviewSection";
import { ConversationViewModal } from "../components/dm/ConversationViewModal";
import MarkedLeadsTab from "../components/dm/MarkedLeadsTab";

// =============================================================================
// DM ANALYTICS PAGE (V3)
// =============================================================================
// PURPOSE: Instagram DM analytics with two-tab structure:
// - Tab 1: Setter Performance (scorecards, work patterns)
// - Tab 2: DM Health (funnel buckets, volume stats, charts, recent conversations)
// - Tab 3: Admin (message pattern review - hidden from non-admins in future)
//
// V3 CHANGES:
// - Split from single scrollable page into focused tabs
// - Each tab has its own purpose: team management vs lead flow health
// - Shared date range and organization context
//
// REQUIREMENTS:
// - Timezone-aware date grouping for daily metrics (AT TIME ZONE org.timezone)
// - Setter attribution: Patil, Alice, Alyssa + ManyChat automation detection
// - Multi-channel support: Instagram, SMS, WhatsApp, Email
// - Delta sync via GHL API (N8N workflow every 40 seconds)
// - Cache strategy with localStorage TTL
//
// See docs/DM_ANALYTICS_V3_SPEC.md for full specification.
// =============================================================================

type TabType = "health" | "setter" | "admin" | "marked";

const TAB_CONFIG: Record<TabType, { label: string }> = {
  marked: { label: "Marked Leads" },
  setter: { label: "Setter Performance" },
  health: { label: "DM Health" },
  admin: { label: "Admin" },
};

const DMAnalyticsPage: React.FC = () => {
  const { currentOrganization } = useData();

  // Compute initial date range synchronously — eliminates the "Initializing date range..." spinner.
  // Uses org timezone to determine today/yesterday (before 11AM = use yesterday).
  const computeInitialDateRange = (): { start: Date | null; end: Date | null } => {
    const tz = currentOrganization?.timezone;
    if (!tz) return { start: null, end: null };

    const now = new Date().toISOString();
    const parts = getPartsInTZ(now, tz);

    let targetYear = parts.year;
    let targetMonth = parts.month;
    let targetDate = parts.date;

    if (parts.hours < 11) {
      const yesterday = new Date(parts.year, parts.month, parts.date - 1);
      targetYear = yesterday.getFullYear();
      targetMonth = yesterday.getMonth();
      targetDate = yesterday.getDate();
    }

    return {
      start: startOfDayInTZ(targetYear, targetMonth, targetDate, tz),
      end: endOfDayInTZ(targetYear, targetMonth, targetDate, tz),
    };
  };

  // Local date range state — try synchronous init, fallback to useEffect if org isn't ready yet
  const [dateRange, setDateRange] = useState(computeInitialDateRange);
  const dateRangeInitialized = useRef(dateRange.start !== null);

  // Fallback: if org wasn't available at mount, set date range when it becomes available.
  // This is a one-time initialization from an async context value, not a cascading render.
  useEffect(() => {
    if (dateRangeInitialized.current) return;
    if (!currentOrganization?.timezone) return;

    const range = computeInitialDateRange();
    if (range.start) {
      dateRangeInitialized.current = true;
      // Deferred to satisfy React Compiler (no sync setState in effect body)
      queueMicrotask(() => setDateRange(range));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization?.timezone]);

  const navigateDateRange = useCallback((direction: "prev" | "next") => {
    setDateRange((prev) => {
      const result = calcNavigateDateRange(prev, direction);
      return result || prev;
    });
  }, []);

  // Tab state (Setter Performance is default/first tab)
  const [activeTab, setActiveTab] = useState<TabType>("setter");

  // State for conversation view modal (used by PatternReviewSection)
  const [selectedConversation, setSelectedConversation] = useState<{
    id: string;
    contactName: string;
    leadInstagram: string | null;
  } | null>(null);

  // Normalize date range: start → midnight, end → 23:59:59.999
  // Fixes single-day selections where start === end === midnight (returns no data)
  const normalizedDateRange = useMemo(() => {
    if (!dateRange.start || !dateRange.end) return dateRange;
    const start = new Date(dateRange.start);
    start.setHours(0, 0, 0, 0);
    const end = new Date(dateRange.end);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }, [dateRange]);

  if (!currentOrganization) {
    return (
      <div className="p-8 text-center text-glass-secondary">Please select an organization.</div>
    );
  }

  // Don't render tabs until date range is initialized
  // (prevents fetching ALL data with null dates on initial render)
  const dateRangeReady = dateRange.start !== null && dateRange.end !== null;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="token-24h text-glass">DM Analytics</h1>
          <p className="token-14 text-glass-secondary mt-1">
            Transparent breakdown of Instagram DM performance
          </p>
        </div>
        {activeTab !== "marked" && (
          <DateRangePicker
            dateRange={dateRange}
            setDateRange={setDateRange}
            navigateDateRange={navigateDateRange}
          />
        )}
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-black/[0.12]">
        <nav className="flex gap-1 overflow-x-auto flex-nowrap" aria-label="Tabs">
          {(Object.keys(TAB_CONFIG) as TabType[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`anim-tap whitespace-nowrap flex-shrink-0 px-4 py-2.5 token-14 font-medium rounded-t-lg transition-colors ${
                activeTab === tab
                  ? "liquid-glass-light border-t border-l border-r border-black/[0.12] text-ds-accent -mb-px"
                  : "text-glass-muted hover:bg-black/[0.06]"
              }`}
            >
              {TAB_CONFIG[tab].label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[600px]">
        {!dateRangeReady ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            <span className="ml-3 text-glass-muted">Initializing date range...</span>
          </div>
        ) : (
          // Keyed by tab so each switch re-mounts the wrapper → .anim-pop fade-up on every tab change
          <div key={activeTab} className="anim-pop">
            {activeTab === "health" && (
              <DMHealthTab
                organizationId={currentOrganization.id}
                dateRange={normalizedDateRange}
                timezone={currentOrganization.timezone || "UTC"}
              />
            )}

            {activeTab === "marked" && <MarkedLeadsTab />}

            {activeTab === "setter" && <SetterPerformanceTab dateRange={normalizedDateRange} />}

            {activeTab === "admin" && (
              <div className="space-y-6">
                <div className="border-l-4 border-orange-500 pl-4">
                  <h2 className="token-18 text-glass mb-4">Message Pattern Review</h2>
                </div>
                <PatternReviewSection
                  organizationId={currentOrganization.id}
                  timezone={currentOrganization.timezone || "UTC"}
                  onConversationClick={(id, name, instagram) => {
                    setSelectedConversation({ id, contactName: name, leadInstagram: instagram });
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Conversation View Modal (for PatternReviewSection clicks) */}
      {selectedConversation && (
        <ConversationViewModal
          isOpen={!!selectedConversation}
          onClose={() => setSelectedConversation(null)}
          conversationId={selectedConversation.id}
          contactName={selectedConversation.contactName}
          leadInstagram={selectedConversation.leadInstagram}
          organizationId={currentOrganization.id}
          timezone={currentOrganization.timezone || "UTC"}
          activeDateRange={
            normalizedDateRange.start && normalizedDateRange.end
              ? { start: normalizedDateRange.start, end: normalizedDateRange.end }
              : null
          }
        />
      )}
    </div>
  );
};

export default DMAnalyticsPage;
